import { CountrySelector } from '@bridebook/web/app-basic/components/country-selector/country-selector';
import { getAlternateLinks, getIsDesktop } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import Router from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import gazetteer, { CountryCodes, Market } from '@bridebook/toolbox/src/gazetteer';
import { Box, ButtonV2, ICountryOption, LoadingCircle } from '@bridebook/ui';
import BridebookLogo from '@bridebook/ui/src/components/svg/bridebook-logo';
import { IconChevronRight } from '@bridebook/ui/src/icons';
import { getAlternativeMarketUrl } from '@bridebook/web/lib/landing/get-alternative-market-url';
import { getCareersUrl } from '@bridebook/web/lib/url-helper';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import componentStyles from './global-home.style';
import { getLocalisedCanonical } from '@bridebook/web/lib/utils/i18n-routing/get-localised-canonical';
import { indexableMarkets } from '@bridebook/toolbox/src/i18n/features';
import { Link } from '@bridebook/web/app-shared/components/link/link';

interface IProps {
  viewerLocation: CountryCodes;
}

export const GlobalHomePage = ({ viewerLocation }: IProps) => {
  const { t } = useTranslation('landing');
  const [selected, setSelected] = useState<ICountryOption | undefined>();
  const isDesktop = useSelector(getIsDesktop);
  const styles = componentStyles();
  const initialMarket = gazetteer.getMarketByCountry(viewerLocation);
  const pathname = usePathWithoutMarket();
  const alternateLinks = useSelector(getAlternateLinks);
  const [showLoader, setShowLoader] = useState(false);

  const onClick = useCallback(() => {
    const market = selected?.market || initialMarket;
    const marketUrl = getAlternativeMarketUrl(market, pathname, alternateLinks);
    setShowLoader(true);
    Router.push(marketUrl, undefined, { locale: false });
  }, [alternateLinks, initialMarket, pathname, selected?.market]);

  const isButtonDisabled = useMemo(
    () =>
      !selected &&
      (initialMarket.flags.blocked ||
        !initialMarket.getLocaleFlags()?.public ||
        !initialMarket.hasAnyDirectory),
    [initialMarket, selected],
  );

  return (
    <Box style={styles.wrapper}>
      <Box style={styles.logoWrap}>
        <BridebookLogo color="indigoCrush" width={isDesktop ? 300 : 190} />
      </Box>

      <Box style={styles.contentWrap}>
        <Box style={styles.dropdown}>
          <Box>{t('globalHome.countrySelector.header')}</Box>
          <CountrySelector
            onSelectCallback={setSelected}
            initialSelection={initialMarket}
            redirectOnClick={false}
          />
          <ButtonV2
            onClick={onClick}
            maxW="max-content"
            styleWrapper={styles.button}
            disabled={isButtonDisabled}>
            {t('globalHome.continue')}
          </ButtonV2>
          {showLoader && (
            <Box mt={10} alignItems="center">
              <LoadingCircle size={40} color="indigoCrush" />
            </Box>
          )}
        </Box>

        <Box as="nav" style={styles.hiddenMarketLinks}>
          {indexableMarkets.map((indexableMarket: Market) => (
            <Link
              title={indexableMarket.getCountryName()}
              key={indexableMarket.prefix}
              href={getLocalisedCanonical(indexableMarket, indexableMarket.prefix)}>
              {indexableMarket.getCountryName()}
            </Link>
          ))}
        </Box>

        <Box style={styles.contentBottom}>
          <Box as="h4" style={styles.contentHeader}>
            {t('globalHome.info.header')}
          </Box>
          <Box style={styles.contentText}>{t('globalHome.info.text')}</Box>

          <a href={getCareersUrl('global')} target="_blank" rel="noopener noreferrer">
            <Box style={styles.careersBtn}>
              <span>{t('globalHome.hiring')}</span>
              <IconChevronRight color="space60" />
            </Box>
          </a>
        </Box>
      </Box>

      <Box style={styles.hero}>
        <Box style={styles.heroContent}>
          <Box as="h1" style={styles.headline}>
            {t('globalHome.hero.headline')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
